import {connect} from 'react-redux';

import {LinkedConversationStatusesEnum} from '../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {getHasAutoShareDrafts} from '../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ResolveStateProps} from '../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {CellsContext} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  createSelector,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {ConversationSearchResultModelWithOptionalConversation} from '../../../../srcCommon/inboxes/search/cellsSearchResultsSelectors';
import {ConversationHighlightModel} from '../../../models/api/conversationHighlightModel';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {ConversationSendersModel} from '../../../models/api/conversationSendersModel';
import {AppState} from '../../../store/appState';
import {getCompany} from '../../../store/data/company/companySelectors';
import {makeGetVisualRecipient} from '../../../store/data/contacts/recipientsSelectors';
import {
  makeGetAssignee,
  makeGetCellCommentingTeammate,
  makeGetCellPrecogs,
  makeGetCellReplyingTeammate,
  makeGetCommentDraftSummaryWithAuthor,
  makeGetConversationInbox,
  makeGetConversationTags,
  makeGetDate,
  makeGetDiscussionTrackerTeammates,
  makeGetDisplayableCommentDraftSummary,
  makeGetIsStarred,
  makeGetLastComment,
  makeGetLastCommentWithAuthor,
  makeGetLastDraftSeedAuthors,
  makeGetLastManualMessage,
  makeGetNumMessages,
  makeGetReminderDate,
  makeGetStatus,
  makeGetTeam,
  makeGetTrackerStatus,
  makeGetVisualSearchActivity,
} from '../../../store/data/conversations/conversationCellSelectors';
import {makeGetVisualConversationSenders} from '../../../store/data/conversations/conversationSendersSelectors';
import {makeGetDraftComments} from '../../../store/data/conversations/conversationsSelectors';
import {
  makeGetConversationDraftSeeds,
  makeGetDraft,
  makeGetLastCommentDraftSummary,
  makeGetSearchResultCommentSummary,
  makeGetUnreadStatusForConversationSearchResult,
} from '../../../store/data/conversationSearchResults/conversationSearchResultCellSelectors';
import {makeGetEarliestSlaBreach} from '../../../store/data/rules/ruleBundlesSelectors';
import {getMentionableAliases} from '../../../store/data/teammates/mentionTeammatesSelectors';
import {getUserId} from '../../../store/data/teammates/teammatesSelectors';
import {InboxesConversationsParametersWithSearch} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {
  computeIsExactlyOneIdSelected,
  computeIsIdSelected,
  findParametersViewId,
  InboxesSelection,
} from '../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {ConversationCell} from './conversationCell';
import {makeGetShouldRenderConversationSenders} from './conversationCellCommon';

/*
 * Props.
 */

export interface ConversationSearchResultCellContainerProps {
  parameters: InboxesConversationsParametersWithSearch | undefined;
  cellsContext: CellsContext;
  searchResult: ConversationSearchResultModelWithOptionalConversation;
  selection: InboxesSelection<number>;
  conversationHighlight?: ConversationHighlightModel;
}

/*
 * Selectors.
 */

function getSearchResult(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.searchResult;
}

function getConversationBase(
  _state: AppState,
  props: ConversationSearchResultCellContainerProps,
): ConversationBaseModel {
  // Return most up-to-date conversation base.
  return props.searchResult.conversation ?? props.searchResult;
}

function getCellsContext(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.cellsContext;
}

function getDelegatedTeammateId(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.cellsContext.teammateId;
}

function getParameters(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.parameters;
}

function getSelection(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.selection;
}

function getSearchActivity(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.conversationHighlight?.searchActivity;
}

function getFullConversation(_state: AppState, props: ConversationSearchResultCellContainerProps) {
  return props.searchResult.conversation;
}

function makeGetShouldUseMessageForDragAndDrop() {
  return createSelector(
    [getSearchResult, getSelection],
    (searchResult, selection) =>
      !computeIsIdSelected(selection, searchResult.id) || computeIsExactlyOneIdSelected(selection),
  );
}

function makeGetIsOriginalConversation() {
  return createSelector(
    [getConversationBase],
    (conversationBase) => conversationBase.linkStatus === LinkedConversationStatusesEnum.ORIGINAL,
  );
}

function getSenders(
  _state: AppState,
  props: ConversationSearchResultCellContainerProps,
): ConversationSendersModel | undefined {
  return props.searchResult.senders;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ConversationSearchResultCellContainerProps>()(() => {
  const getVisualUnreadStatus = makeGetUnreadStatusForConversationSearchResult(
    getSearchResult,
    getCellsContext,
  );
  const getCellPrecogs = wrapInShallowSelector(
    makeGetCellPrecogs(getConversationBase, getVisualUnreadStatus),
  );
  const getTrackerTeammates = wrapInShallowSelector(
    makeGetDiscussionTrackerTeammates(getConversationBase, getDelegatedTeammateId),
  );
  const getConversationTags = wrapInShallowSelector(makeGetConversationTags(getConversationBase));
  const getCommentDraftSummary = makeGetDisplayableCommentDraftSummary(
    makeGetLastCommentDraftSummary(getSearchResult),
  );

  const getDraftComments = makeGetDraftComments(getFullConversation);
  const getLastComment = makeGetLastComment(getFullConversation, getDraftComments);

  const getVisualConversationSenders = makeGetVisualConversationSenders(
    getConversationBase,
    getUserId,
    getSenders,
  );

  return {
    // ConversationBase.
    date: makeGetDate(getConversationBase, getDelegatedTeammateId, getParameters, getCellsContext),
    status: makeGetStatus(getCellsContext, getConversationBase),
    trackerStatus: makeGetTrackerStatus(getDelegatedTeammateId, getConversationBase),
    trackerTeammates: getTrackerTeammates,
    tags: getConversationTags,
    recipient: makeGetVisualRecipient(
      getConversationBase,
      getDelegatedTeammateId,
      'conversationSearchResultCellContainer',
    ),
    assignee: makeGetAssignee(getConversationBase, getParameters),
    team: makeGetTeam(getConversationBase, getCellsContext),
    inbox: makeGetConversationInbox(getConversationBase, getParameters, getCellsContext),
    commentingTeammate: makeGetCellCommentingTeammate(getCellPrecogs),
    replyingTeammate: makeGetCellReplyingTeammate(getCellPrecogs),
    reminderDate: makeGetReminderDate(getConversationBase, getCellsContext),
    mentionableAliases: getMentionableAliases,
    shouldUseMessageForDragAndDrop: makeGetShouldUseMessageForDragAndDrop(),
    isStarred: makeGetIsStarred(getConversationBase, getDelegatedTeammateId),
    isOriginalConversation: makeGetIsOriginalConversation(),
    userId: getUserId,
    numMessages: makeGetNumMessages(getConversationBase),
    conversation: getConversationBase,
    hasAutoShareDrafts: getHasAutoShareDrafts,
    slaTimerDate: makeGetEarliestSlaBreach(getConversationBase),

    // Search result specific.
    searchResultCommentSummary: makeGetSearchResultCommentSummary(getSearchResult),
    searchActivity: makeGetVisualSearchActivity(getSearchActivity),
    conversationSenders: getVisualConversationSenders,

    // Full conversation enhances those.
    unreadStatus: getVisualUnreadStatus,
    draft: makeGetDraft(getSearchResult, getFullConversation),
    commentDraftSummary: makeGetCommentDraftSummaryWithAuthor(getCommentDraftSummary),
    draftSeedAuthors: makeGetLastDraftSeedAuthors(makeGetConversationDraftSeeds(getSearchResult)),

    // Full conversation, when present.
    lastComment: makeGetLastCommentWithAuthor(getLastComment),
    lastManualMessage: makeGetLastManualMessage(getFullConversation),
    company: getCompany,
    isSearch: () => true,
    shouldRenderConversationSenders: makeGetShouldRenderConversationSenders(
      getVisualConversationSenders,
      () => true,
    ),
  };
});

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  _dispatchProps: unknown,
  ownProps: ConversationSearchResultCellContainerProps,
) {
  // Remove parameters from props since it's not stable and not used.
  const {parameters, ...remainingOwnProps} = ownProps;
  const sourceCustomViewId = parameters && findParametersViewId(parameters);

  return {
    sourceCustomViewId,
    ...remainingOwnProps,
    ...stateProps,
  };
}

/*
 * Component.
 */

export const ConversationSearchResultCellContainer = connect(mapStateToProps, {}, mergeProps, {
  forwardRef: true,
})(ConversationCell);
